import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  createInstruction,
  deleteInstruction,
  fetchInstructions,
  updateInstruction
} from '../../../../actions/aiInstructionsActions';
import ModalBoxes from '../../../../common/feedback/ModalBoxes/ModalBoxes';
import { INSTRUCTION_VISIBILITY } from '../../../../constants/aiAnalytics';
import { MANAGE_GLOBAL_AI_INSTRUCTIONS } from '../../../../constants/userOperations';
import { userHasAccessTo } from '../../../../services/auth';
import { AI_COLORS } from '../aiAnalyticsConstants';

import InstructionCard from './InstructionCard';
import InstructionDialog from './InstructionDialog';

export default function InstructionsPanel() {
  const dispatch = useDispatch();
  const { instructions, instructionsLoading } = useSelector(state => state.aiAnalytics);
  const canManageGlobal = userHasAccessTo(MANAGE_GLOBAL_AI_INSTRUCTIONS);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(fetchInstructions());
  }, [dispatch]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return instructions;
    }
    return instructions.filter(
      i => (i.title || '').toLowerCase().includes(term) || (i.details || '').toLowerCase().includes(term)
    );
  }, [instructions, search]);

  function handleAdd() {
    setEditing(null);
    setDialogOpen(true);
  }

  function handleEdit(instruction) {
    setEditing(instruction);
    setDialogOpen(true);
  }

  async function handleSave(payload) {
    const result = editing
      ? await dispatch(updateInstruction(editing.id, payload, editing.visibility))
      : await dispatch(createInstruction(payload));
    if (result) {
      setDialogOpen(false);
    }
  }

  function handleDelete(instruction) {
    ModalBoxes.confirm({
      content: 'Are you sure you want to delete this instruction?',
      confirmButton: 'Yes',
      cancelButton: 'No'
    }).then(
      () => {
        dispatch(deleteInstruction(instruction.id, instruction.visibility));
      },
      () => {}
    );
  }

  return (
    <Box className="instructions-panel">
      <Box className="instructions-panel-header">
        <Typography variant="subtitle2" sx={{ letterSpacing: 0.5 }}>
          INSTRUCTIONS
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          data-testid="ai-instructions-add-btn"
          sx={{ textTransform: 'none', color: AI_COLORS.primary }}
        >
          Add
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Instructions are automatically applied to every report query.
      </Typography>

      <TextField
        size="small"
        fullWidth
        placeholder="Search"
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{ mb: 1.5 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          )
        }}
      />

      {filtered.map(instruction => (
        <InstructionCard
          key={instruction.id}
          instruction={instruction}
          disabled={instructionsLoading}
          readOnly={!canManageGlobal && instruction.visibility === INSTRUCTION_VISIBILITY.GLOBAL}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ))}

      {instructions.length === 0 && !instructionsLoading && (
        <Typography variant="body2" color="text.secondary">
          No instructions yet.
        </Typography>
      )}
      {instructions.length > 0 && filtered.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No instructions match your search.
        </Typography>
      )}

      <InstructionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        instruction={editing}
        canManageGlobal={canManageGlobal}
      />
    </Box>
  );
}
